import type {
  AvailabilitySlot,
  CreateBookingResult,
  ExperienceOption,
  PaymentMethod,
} from '@flyingmuse/shared';
import type { Experience } from '../../data/experiences';
import { monthKey, monthOf } from './format';

export type Step =
  | 'experience'
  | 'option'
  | 'dateGuests'
  | 'contact'
  | 'review'
  | 'success';
export type Loadable = 'idle' | 'loading' | 'ready' | 'error';

export interface ContactDraft {
  fullName: string;
  email: string;
  phone: string;
}

export interface FlowState {
  step: Step;
  /** The chosen experience (null until picked on the experience step). */
  experience: Experience | null;
  options: ExperienceOption[];
  optionsStatus: Loadable;
  selectedOptionId: number | null;
  /**
   * Availability is loaded and browsed one month at a time — LinkTwist rejects
   * any availability request wider than 31 days, so a month is both the natural
   * UI unit and the largest window we are allowed to ask for.
   */
  selectedMonth: string;
  slotsByMonth: Record<string, AvailabilitySlot[]>;
  monthStatus: Record<string, Loadable>;
  /** Chosen calendar day (yyyy-MM-dd); picked before a time slot. */
  selectedDay: string | null;
  /** Chosen time slot (full date-time); set once a day + time are picked. */
  selectedDate: string | null;
  /** participant type alias -> quantity */
  quantities: Record<string, number>;
  contact: ContactDraft;
  comments: string;
  /** How the guest chose to settle — picked on the review step. */
  paymentMethod: PaymentMethod;
  submitting: boolean;
  error: string | null;
  result: CreateBookingResult | null;
}

export type Action =
  | { type: 'SELECT_EXPERIENCE'; experience: Experience }
  | { type: 'OPTIONS_LOADING' }
  | { type: 'OPTIONS_READY'; options: ExperienceOption[]; initialOptionId?: number }
  | { type: 'OPTIONS_ERROR' }
  | { type: 'SELECT_OPTION'; optionId: number }
  | { type: 'SELECT_MONTH'; month: string }
  | { type: 'SLOTS_LOADING'; month: string }
  | { type: 'SLOTS_READY'; month: string; slots: AvailabilitySlot[] }
  | { type: 'SLOTS_ERROR'; month: string }
  | { type: 'SELECT_DAY'; day: string }
  | { type: 'SELECT_DATE'; date: string }
  | { type: 'SET_QTY'; alias: string; quantity: number }
  | { type: 'SET_CONTACT'; patch: Partial<ContactDraft> }
  | { type: 'SET_COMMENTS'; comments: string }
  | { type: 'SET_PAYMENT_METHOD'; paymentMethod: PaymentMethod }
  | { type: 'GO'; step: Step }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_ERROR'; error: string }
  | { type: 'SUBMIT_SUCCESS'; result: CreateBookingResult };

/**
 * Payment methods offered in the wizard, in display order.
 *
 * Card-only: every booking is paid online. Pay-on-arrival is intentionally
 * *disabled rather than deleted* — the `createBooking` callable and its whole
 * server path still work, so putting `'arrival'` back in this list is all it
 * takes to offer it again. When only one method is enabled the wizard hides
 * the chooser entirely instead of showing a single pointless option.
 */
export const ENABLED_PAYMENT_METHODS: PaymentMethod[] = ['card'];

export const DEFAULT_PAYMENT_METHOD: PaymentMethod =
  ENABLED_PAYMENT_METHODS[0] ?? 'card';

export const initialFlowState: FlowState = {
  step: 'experience',
  experience: null,
  options: [],
  optionsStatus: 'idle',
  selectedOptionId: null,
  selectedMonth: monthKey(new Date()),
  slotsByMonth: {},
  monthStatus: {},
  selectedDay: null,
  selectedDate: null,
  quantities: {},
  contact: { fullName: '', email: '', phone: '' },
  comments: '',
  paymentMethod: DEFAULT_PAYMENT_METHOD,
  submitting: false,
  error: null,
  result: null,
};

/* ------------------------------------------------------------------ */
/* Guest rules                                                         */
/* ------------------------------------------------------------------ */

export interface GuestRule {
  /** Minimum total guests required to book this option. */
  min: number;
  /** When true the guest count is fixed at `min` (e.g. a couple is always 2). */
  locked: boolean;
}

/**
 * Business rules layered on top of LinkTwist's own participant minimums:
 *   - Solo packages allow a single guest.
 *   - Couple packages are always exactly 2 guests (locked).
 *   - Every other (non-solo) package requires at least 2 guests.
 */
export function guestRule(option: ExperienceOption): GuestRule {
  const t = `${option.title} ${option.alias}`.toLowerCase();
  if (t.includes('couple')) return { min: 2, locked: true };
  if (t.includes('solo')) return { min: 1, locked: false };
  return { min: 2, locked: false };
}

/** Sensible starting quantities, honouring the option's guest rule. */
export function defaultQuantities(
  option: ExperienceOption,
): Record<string, number> {
  const rule = guestRule(option);
  const q: Record<string, number> = {};
  let primarySeeded = false;
  for (const pt of option.participantTypes) {
    if (pt.isPrimary && !primarySeeded) {
      // The first primary type carries the rule minimum (e.g. couple → 2).
      q[pt.alias] = Math.max(rule.min, pt.minOrder, 1);
      primarySeeded = true;
    } else {
      q[pt.alias] = pt.isPrimary ? Math.max(1, pt.minOrder) : 0;
    }
  }
  return q;
}

/** Whether the chosen quantities satisfy the option's guest rule. */
export function guestsValid(
  option: ExperienceOption,
  quantities: Record<string, number>,
): boolean {
  const rule = guestRule(option);
  const total = Object.values(quantities).reduce((n, q) => n + q, 0);
  if (total < rule.min) return false;
  if (rule.locked && total !== rule.min) return false;
  return option.participantTypes.every((pt) =>
    pt.isPrimary ? (quantities[pt.alias] ?? 0) >= Math.max(1, pt.minOrder) : true,
  );
}

function selectOption(state: FlowState, optionId: number): FlowState {
  const option = state.options.find((o) => o.id === optionId);
  return {
    ...state,
    selectedOptionId: optionId,
    quantities: option ? defaultQuantities(option) : {},
    // Availability is per option, so everything loaded so far is now stale.
    selectedMonth: monthKey(new Date()),
    slotsByMonth: {},
    monthStatus: {},
    selectedDay: null,
    selectedDate: null,
    step: 'dateGuests',
  };
}

export function flowReducer(state: FlowState, action: Action): FlowState {
  switch (action.type) {
    case 'SELECT_EXPERIENCE':
      // Re-picking the same experience just advances (options already loaded).
      if (state.experience?.id === action.experience.id) {
        return { ...state, step: 'option', error: null };
      }
      return {
        ...state,
        experience: action.experience,
        options: [],
        optionsStatus: 'idle',
        selectedOptionId: null,
        selectedMonth: monthKey(new Date()),
        slotsByMonth: {},
        monthStatus: {},
        selectedDay: null,
        selectedDate: null,
        quantities: {},
        error: null,
        step: 'option',
      };
    case 'OPTIONS_LOADING':
      return { ...state, optionsStatus: 'loading' };
    case 'OPTIONS_READY': {
      const next = { ...state, options: action.options, optionsStatus: 'ready' as const };
      const preselect =
        action.initialOptionId &&
        action.options.find((o) => o.id === action.initialOptionId)
          ? action.initialOptionId
          : action.options.length === 1
            ? action.options[0].id
            : null;
      if (preselect) return selectOption(next, preselect);
      return { ...next, step: 'option' };
    }
    case 'OPTIONS_ERROR':
      return { ...state, optionsStatus: 'error' };
    case 'SELECT_OPTION':
      return selectOption(state, action.optionId);
    case 'SELECT_MONTH':
      // The guest's chosen slot survives a month change — they can browse away
      // and come back without losing it.
      return { ...state, selectedMonth: action.month, error: null };
    case 'SLOTS_LOADING':
      return {
        ...state,
        monthStatus: { ...state.monthStatus, [action.month]: 'loading' },
      };
    case 'SLOTS_READY':
      return {
        ...state,
        slotsByMonth: { ...state.slotsByMonth, [action.month]: action.slots },
        monthStatus: { ...state.monthStatus, [action.month]: 'ready' },
      };
    case 'SLOTS_ERROR':
      return {
        ...state,
        monthStatus: { ...state.monthStatus, [action.month]: 'error' },
      };
    case 'SELECT_DAY':
      // Picking a (different) day clears any previously chosen time slot.
      if (state.selectedDay === action.day) return state;
      return { ...state, selectedDay: action.day, selectedDate: null };
    case 'SELECT_DATE':
      return { ...state, selectedDate: action.date };
    case 'SET_QTY':
      return {
        ...state,
        quantities: { ...state.quantities, [action.alias]: Math.max(0, action.quantity) },
      };
    case 'SET_CONTACT':
      return { ...state, contact: { ...state.contact, ...action.patch } };
    case 'SET_COMMENTS':
      return { ...state, comments: action.comments };
    case 'SET_PAYMENT_METHOD':
      return { ...state, paymentMethod: action.paymentMethod, error: null };
    case 'GO':
      return { ...state, step: action.step, error: null };
    case 'SUBMIT_START':
      return { ...state, submitting: true, error: null };
    case 'SUBMIT_ERROR':
      return { ...state, submitting: false, error: action.error };
    case 'SUBMIT_SUCCESS':
      return { ...state, submitting: false, result: action.result, step: 'success' };
    default:
      return state;
  }
}

/* ------------------------------------------------------------------ */
/* Selectors / helpers                                                 */
/* ------------------------------------------------------------------ */

export function selectedOption(state: FlowState): ExperienceOption | undefined {
  return state.options.find((o) => o.id === state.selectedOptionId);
}

/** Slots for the month currently on screen. */
export function monthSlots(state: FlowState): AvailabilitySlot[] {
  return state.slotsByMonth[state.selectedMonth] ?? [];
}

/** Load state of the month currently on screen. */
export function monthSlotsStatus(state: FlowState): Loadable {
  return state.monthStatus[state.selectedMonth] ?? 'idle';
}

/**
 * The chosen slot, looked up in the month it belongs to rather than the one on
 * screen — browsing to another month must not drop the guest's selection.
 */
export function selectedSlot(state: FlowState): AvailabilitySlot | undefined {
  if (!state.selectedDate) return undefined;
  const month = state.slotsByMonth[monthOf(state.selectedDate)];
  return month?.find((s) => s.dateTime === state.selectedDate);
}

export function totalGuests(state: FlowState): number {
  return Object.values(state.quantities).reduce((n, q) => n + q, 0);
}
